/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Box, Grid } from '@mui/material';
import { ImperativeModalHandler, Modal } from 'components/Modal/Modal';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from 'store';
import { AnswerSubfield, Field, FormState } from 'store/states/forms/types';
import { Historic } from 'store/states/historics/types';

import { verifyRules } from 'utils/fieldNotebookUtils/verifyRules';
import { HistoricInputSelector } from '../components/HistoricInputSelector/HistoricInputSelector';
import { Label } from '../components/Label/Label';
import { ThumbnailFullImage } from '../components/ThumbnailFullImage/ThumbnailFullImage';
import { ShowFullImage } from './styles';

interface HistoricContentProps {
  historic: Historic;
}

function isPdfUrl(url: string): boolean {
  return url.includes('response-content-type=application%2Fpdf');
}

function isNonEmptyCheckboxGroup(
  field: Field,
  answer: string | AnswerSubfield[] | null | undefined,
): boolean {
  if (field.type !== 'checkbox_group') return false;
  if (answer == null || answer === '') return false;
  try {
    const parsed =
      typeof answer === 'string'
        ? (JSON.parse(answer) as AnswerSubfield[])
        : answer;
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

export function HistoricContent({ historic }: HistoricContentProps) {
  const { selectedForm } = useSelector<ReduxState, FormState>(
    (state) => state.forms,
  );

  const imageModalRef = useRef<ImperativeModalHandler>(null);
  const galleryModalRef = useRef<ImperativeModalHandler>(null);
  const thumbnailModalRef = useRef<ImperativeModalHandler>(null);
  const drawModalRef = useRef<ImperativeModalHandler>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);
  const [signatureUrl, setSignatureUrl] = useState('');
  const [drawUrl, setDrawUrl] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalSubtitle, setModalSubtitle] = useState('');
  const handleOpenImageModal = (
    imageurl: string,
    title: string,
    subtitle: string,
  ) => {
    setImageUrl(imageurl);
    setModalTitle(title);
    setModalSubtitle(subtitle);
    imageModalRef.current?.open();
  };

  const handleOpenImageGalleryModal = (
    urls: string[],
    title: string,
    subtitle: string,
  ) => {
    setGalleryUrls(urls);
    setModalTitle(title);
    setModalSubtitle(subtitle);
    galleryModalRef.current?.open();
  };

  const handleOpenThumbnailModal = (
    signature: string,
    title: string,
    subtitle: string,
  ) => {
    setSignatureUrl(signature);
    setModalTitle(title);
    setModalSubtitle(subtitle);
    thumbnailModalRef.current?.open();
  };

  const handleOpenDrawModal = (
    draw: string,
    title: string,
    subtitle: string,
  ) => {
    setDrawUrl(draw);
    setModalTitle(title);
    setModalSubtitle(subtitle);
    drawModalRef.current?.open();
  };

  return (
    <>
      <Grid container spacing={3} mb={1}>
        {selectedForm.fields.map((field, index) => {
          const answer = historic.answers.filter(
            (a) => a.field_id === field.field_id,
          )[0];
          const rulesSatisfied = verifyRules(
            field,
            historic.answers.map((historyAnswer) => ({
              field_id: historyAnswer.field_id,
              value: historyAnswer.answer,
            })),
          );
          const isFullWidth = isNonEmptyCheckboxGroup(field, answer?.answer);

          return (
            rulesSatisfied && (
              <Grid
                item
                xs={12}
                md={isFullWidth ? 12 : 4}
                sm={isFullWidth ? 12 : 6}
                key={index}
              >
                {isFullWidth ? (
                  <>
                    <Label
                      dangerouslySetInnerHTML={{ __html: field.description }}
                      sx={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: 'primary.main',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    />
                    <Box
                      sx={{
                        bgcolor: '#F0F0F0',
                        borderRadius: 1,
                        p: 2,
                      }}
                    >
                      <HistoricInputSelector
                        field={field}
                        answer={answer?.answer as string}
                        formDate={historic.form_date}
                        openImageModal={handleOpenImageModal}
                        openImageGalleryModal={handleOpenImageGalleryModal}
                        openThumbnailModal={handleOpenThumbnailModal}
                        openDrawModal={handleOpenDrawModal}
                      />
                    </Box>
                  </>
                ) : (
                  <>
                    <Label
                      dangerouslySetInnerHTML={{ __html: field.description }}
                      sx={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: 'primary.main',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    />
                    <HistoricInputSelector
                      field={field}
                      answer={answer?.answer as string}
                      formDate={historic.form_date}
                      openImageModal={handleOpenImageModal}
                      openImageGalleryModal={handleOpenImageGalleryModal}
                      openThumbnailModal={handleOpenThumbnailModal}
                      openDrawModal={handleOpenDrawModal}
                    />
                  </>
                )}
              </Grid>
            )
          );
        })}
      </Grid>

      <Modal
        ref={imageModalRef}
        title={modalTitle}
        subtitle={modalSubtitle}
        hasCloseIcon
      >
        <ThumbnailFullImage imageUrl={imageUrl} />
      </Modal>
      <Modal
        ref={galleryModalRef}
        title={modalTitle}
        subtitle={modalSubtitle}
        hasCloseIcon
      >
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            maxHeight: '70vh',
            overflowY: 'auto',
            p: 1,
          }}
        >
          {(galleryUrls.length > 1
            ? [
                galleryUrls.filter((_, i) => i % 2 === 0),
                galleryUrls.filter((_, i) => i % 2 === 1),
              ]
            : [galleryUrls]
          ).map((col, ci) => (
            <Box
              key={ci}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                width: 500,
              }}
            >
              {col.map((url, i) => {
                const pdf = isPdfUrl(url);
                if (pdf) {
                  return (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        height: 200,
                        background: '#fff',
                        border: '1px solid #ddd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textDecoration: 'none',
                        color: '#D32F2F',
                        fontWeight: 700,
                        fontSize: 18,
                        letterSpacing: 1,
                      }}
                    >
                      .PDF
                    </a>
                  );
                }
                return (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'block' }}
                  >
                    <img
                      src={url}
                      alt={`gallery-${i}`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                      }}
                    />
                  </a>
                );
              })}
            </Box>
          ))}
        </Box>
      </Modal>
      <Modal
        ref={thumbnailModalRef}
        title={modalTitle}
        subtitle={modalSubtitle}
        hasCloseIcon
      >
        <ShowFullImage src={signatureUrl} alt="Signature Full Image" />
      </Modal>

      <Modal
        ref={drawModalRef}
        title={modalTitle}
        subtitle={modalSubtitle}
        hasCloseIcon
      >
        <ShowFullImage src={drawUrl} alt="Draw Full Image" />
      </Modal>
    </>
  );
}
